import type { AnnotationState } from '@markerjs/markerjs3';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

const PROMPT_HISTORY_LIMIT = 20;
const VERSION_HISTORY_LIMIT = 20;

export type TFigureVersionSnapshot =
  | { kind: 'image'; baseFileId: string; metadata?: string; fileName: string; takenAt: number }
  | { kind: 'generated'; tempFileId: string; fileName: string; takenAt: number; prompt?: string }
  | { kind: 'diagram'; markerState: AnnotationState; takenAt: number; prompt?: string }
  | { kind: 'drawio'; xml: string; takenAt: number; prompt?: string };

interface IFigureState {
  isFigurePopoutDisabled: boolean;
  livePreviewByFigureId: Record<string, string>;
  promptHistoryByKey: Record<string, string[]>;
  versionHistoryByKey: Record<string, TFigureVersionSnapshot[]>;
}

const initialState: IFigureState = {
  isFigurePopoutDisabled: false,
  livePreviewByFigureId: {},
  promptHistoryByKey: {},
  versionHistoryByKey: {},
};

const figureSlice = createSlice({
  name: 'figure',
  initialState,
  reducers: {
    setIsFigurePopoutDisabled: (state, action: PayloadAction<boolean>) => {
      state.isFigurePopoutDisabled = action.payload;
    },
    setLivePreview: (state, action: PayloadAction<{ figureId: string; url: string }>) => {
      state.livePreviewByFigureId[action.payload.figureId] = action.payload.url;
    },
    clearLivePreview: (state, action: PayloadAction<{ figureId: string }>) => {
      delete state.livePreviewByFigureId[action.payload.figureId];
    },
    addPromptToHistory: (state, action: PayloadAction<{ key: string; prompt: string }>) => {
      const { key, prompt } = action.payload;
      const trimmed = prompt.trim();
      if (!trimmed) return;
      const existing = state.promptHistoryByKey[key] ?? [];
      state.promptHistoryByKey[key] = [trimmed, ...existing.filter(p => p !== trimmed)].slice(0, PROMPT_HISTORY_LIMIT);
    },
    setVersionHistory: (state, action: PayloadAction<{ key: string; versions: TFigureVersionSnapshot[] }>) => {
      state.versionHistoryByKey[action.payload.key] = action.payload.versions.slice(-VERSION_HISTORY_LIMIT);
    },
  },
});

export default figureSlice.reducer;

export const { setIsFigurePopoutDisabled, setLivePreview, clearLivePreview, addPromptToHistory, setVersionHistory } =
  figureSlice.actions;
